/**
 * The outbox: everything between "a ball is on disk" and "the server has acked it".
 *
 * Free of browser-only APIs on purpose — no `fetch`, no `window`, no `navigator`, no
 * `indexedDB`. The transport, the clock, the timer and the random source are all injected,
 * which is what lets React Native reuse this file unchanged (ADR-0010) and, just as
 * importantly, what lets the tests drive a three-hour outage in a millisecond.
 *
 * The timer's *default* uses `setTimeout`, which is not a browser API — Node and React Native
 * both have it. It is injectable anyway, because a scheduler nobody can advance by hand is a
 * scheduler nobody has tested.
 *
 * Three rules run through all of it, and each one is a ball that would otherwise be lost:
 *
 * 1. **An event is synced only on its own ack.** Never on a 2xx for the batch that
 *    contained it. A batch can be accepted while one event inside it is rejected, and
 *    marking the batch synced loses exactly that one.
 * 2. **A rejection is not a reason to drop.** `seq_conflict` means the address is taken, so
 *    the answer is renumber and resend. Dropping is how the product's central promise
 *    breaks.
 * 3. **A gap is loud.** Reported holes are surfaced to the caller, never counted and
 *    forgotten (PRD §7.2).
 */
import { isAccepted } from "@sportsos/engine";
import type {
  AppendEventsResponse,
  Event,
  EventAck,
  EventId,
  InningsNo,
  MatchId,
  SeqGap,
} from "@sportsos/engine";
import { DEFAULT_BACKOFF, backoffDelay } from "./backoff.ts";
import type { BackoffOptions } from "./backoff.ts";
import type { EventStore, StoredEvent } from "./store.ts";

/** The one network call the outbox makes. Implemented over `fetch` by the web app. */
export interface SyncTransport {
  append(matchId: MatchId, events: readonly Event[]): Promise<AppendEventsResponse>;
}

/**
 * What a transport throws when the server says the session is not valid (a 401).
 *
 * Its own type, and not just "an error", because it is the one failure that retrying cannot
 * fix and that backing off would only make worse: no amount of waiting renews a lapsed
 * session, a person has to sign in. Everything else the transport throws is treated as the
 * network being bad.
 */
export class SyncAuthError extends Error {
  constructor(message = "the session is not valid") {
    super(message);
    this.name = "SyncAuthError";
  }
}

/**
 * Run `run` while holding an exclusive lock on `key`, or report that it could not.
 *
 * Resolves true if `run` ran, false if someone else already holds the key. It must never
 * *wait* for the lock: the holder may be a drain that is backing off for minutes, and a
 * second drainer queueing behind it achieves nothing.
 *
 * Injected because the platform's lock (`navigator.locks`) is a browser API, and this file
 * has none. The page and the service worker both pass the same one, which is what stops them
 * draining one match at once. The lock does **not** prevent duplicates — the server dedupes
 * on `eventId` — it protects the on-disk renumbering after a `seq_conflict`, which two
 * drainers rewriting the same event would race on.
 */
export type LockRunner = (key: string, run: () => Promise<void>) => Promise<boolean>;

/**
 * What the pill shows. Exhaustive by construction, so there is no "unknown" state — the
 * scorer being uncertain is the single most damaging failure in this category.
 */
export type SyncState =
  /** Nothing on disk yet for this match. */
  | { readonly kind: "idle" }
  /** Everything on disk has been acked. */
  | { readonly kind: "synced" }
  /** A drain is in flight. */
  | { readonly kind: "syncing"; readonly pending: number }
  /**
   * Written locally, waiting for a drain that has not been attempted or has backed off.
   * Distinct from `offline`: the work is safe either way, but only one of them means
   * "there is nothing wrong".
   */
  | { readonly kind: "saved"; readonly pending: number }
  /** The last attempt failed. The scorer needs to know the queue is not draining. */
  | { readonly kind: "offline"; readonly pending: number }
  /**
   * Events the server refused for a reason retrying cannot fix.
   *
   * A fifth state beyond the four the ticket names, and deliberately so: the requirement
   * is that the pill is *never ambiguous*, and a ball that will never sync showing "Saved
   * on device" is the most ambiguous thing it could say. It is safe on disk and it is not
   * going anywhere, and those are different facts. `onStuck` carries the detail; this is
   * what the pill shows so nobody has to read a log to find out.
   */
  | { readonly kind: "blocked"; readonly pending: number; readonly blocked: number }
  /**
   * The server refused the session. Everything is still on disk; the scorer has to sign in.
   * Not `offline`: waiting will not fix it, and the pill has to say so.
   */
  | { readonly kind: "signin"; readonly pending: number }
  /**
   * Events on disk were written by a different account from the one signed in now. They are
   * held back rather than sent under the wrong person, and never deleted without a
   * confirmation (`discardForeign`).
   */
  | { readonly kind: "foreign"; readonly pending: number; readonly foreign: number };

export interface OutboxEvents {
  /** Called whenever the state the pill renders changes. */
  onStateChange?: (state: SyncState) => void;
  /**
   * A hole the server reported in an innings' `seq`.
   *
   * Surfaced rather than logged, because PRD §7.2 wants a gap to be a visible error. The
   * caller is expected to put it on screen, not count it.
   */
  onGaps?: (gaps: readonly SeqGap[]) => void;
  /**
   * An event the server refused for a reason retrying cannot fix — `malformed`,
   * `unstorable`, `unauthorized`, or a `seq_conflict` held by an event this device has
   * never seen (a second device is writing this innings).
   *
   * Never dropped silently. The event stays on disk and stays unsynced, so the pill keeps
   * showing it, and a human is told.
   */
  onStuck?: (event: Event, ack: EventAck) => void;
}

export interface OutboxOptions extends OutboxEvents {
  readonly store: EventStore;
  readonly transport: SyncTransport;
  readonly matchId: MatchId;
  /** Injected so tests are deterministic and React Native is unaffected. */
  readonly now?: () => Date;
  readonly sleep?: (ms: number) => Promise<void>;
  readonly random?: () => number;
  readonly backoff?: Omit<BackoffOptions, "random">;
  /**
   * How many events to post at once.
   *
   * Batched because a scorer coming back from an outage has three hours of balls to send
   * and one request per ball would take minutes. Bounded because a single failed request
   * should not cost the whole innings a retry.
   */
  readonly batchSize?: number;
  /**
   * The signed-in account's id, or null when it is not known.
   *
   * A function so it is read at drain time: the scorer can sign in as someone else while this
   * outbox lives. Null means "no opinion" and disables the ownership check entirely — the
   * server's own authorisation is then the only gate, which is the position before ownership
   * was recorded.
   */
  readonly owner?: () => string | null;
  /**
   * Refuse to send an event that has an owner while the signed-in account is unknown.
   *
   * Without it, "unknown" means "no opinion" and the server's cookie check is the only gate —
   * which is no gate at all when the cookie belongs to a different person from the one who
   * scored. A boot the server has not answered yet, and a service worker with no identity hint
   * to read, are exactly that situation. Events with no recorded owner (a guest's, or from
   * before ownership existed) are unaffected, and everything is released the moment the
   * account is known. Off by default so a caller that never wires identity is unchanged.
   */
  readonly ownerRequired?: boolean;
  /** See `LockRunner`. Omitted, drains are unguarded — one drainer, as in most tests. */
  readonly withLock?: LockRunner;
  /**
   * Stop after this many consecutive failed requests, leaving the events on disk.
   *
   * Unset, a drain retries with backoff for as long as it takes, which is right for a page
   * that lives as long as the match. A service worker's `sync` event cannot do that: it has
   * to finish and say whether it worked, so the browser can schedule another try.
   */
  readonly maxFailures?: number;
}

/** Attempts before an event is reported stuck. Bounded so a wedged event cannot spin. */
const MAX_CONFLICT_RENUMBERS = 20;

export class Outbox {
  private readonly store: EventStore;
  private readonly transport: SyncTransport;
  private readonly matchId: MatchId;
  private readonly now: () => Date;
  private readonly sleep: (ms: number) => Promise<void>;
  private readonly backoff: BackoffOptions;
  private readonly batchSize: number;
  private readonly events: OutboxEvents;
  private readonly owner: () => string | null;
  private readonly ownerRequired: boolean;
  private readonly withLock: LockRunner | undefined;
  private readonly maxFailures: number;

  private failures = 0;
  /** Set by a 401. While set, a drain sends nothing: the next attempt would fail the same way. */
  private authBlocked = false;
  /**
   * Events given up on, kept on the instance rather than per-drain so a drain terminates
   * instead of re-posting something that cannot land. Cleared by `onNetworkOnline`, which
   * is a genuine "something changed, try again" signal.
   */
  private readonly stuck = new Set<EventId>();
  private draining: Promise<void> | null = null;
  /** Set while a drain is in flight and another is requested, so the queue re-runs once. */
  private again = false;
  private state: SyncState = { kind: "idle" };

  constructor(options: OutboxOptions) {
    this.store = options.store;
    this.transport = options.transport;
    this.matchId = options.matchId;
    this.now = options.now ?? ((): Date => new Date());
    this.sleep =
      options.sleep ??
      ((ms: number): Promise<void> =>
        new Promise((resolve) => {
          setTimeout(resolve, ms);
        }));
    this.backoff = { ...DEFAULT_BACKOFF, ...options.backoff, random: options.random ?? Math.random };
    this.batchSize = options.batchSize ?? 50;
    this.owner = options.owner ?? ((): null => null);
    this.ownerRequired = options.ownerRequired ?? false;
    this.withLock = options.withLock;
    this.maxFailures = options.maxFailures ?? Number.POSITIVE_INFINITY;
    this.events = options;
  }

  current(): SyncState {
    return this.state;
  }

  private setState(next: SyncState): void {
    this.state = next;
    this.events.onStateChange?.(next);
  }

  /**
   * Recompute the pill from disk.
   *
   * Read from the store rather than tracked in a counter, because the counter and the disk
   * drifting apart is precisely the ambiguity the pill exists to remove — a pill that says
   * "Synced" while a ball sits unsent is worse than no pill.
   */
  async refresh(): Promise<SyncState> {
    const pending = await this.store.pendingCount(this.matchId);
    const all = await this.store.getAll(this.matchId);
    const foreign = this.foreignIds(all).length;
    if (all.length === 0) {
      this.setState({ kind: "idle" });
    } else if (pending === 0) {
      this.setState({ kind: "synced" });
    } else if (foreign > 0) {
      // First, because it needs a decision from a person and nothing else on this list does:
      // these balls will not move whatever the network does.
      this.setState({ kind: "foreign", pending, foreign });
    } else if (this.authBlocked) {
      this.setState({ kind: "signin", pending });
    } else if (this.stuck.size > 0) {
      // Checked before `offline`, because "these balls need a human" is more actionable
      // than "the network is down" and the two can be true at once.
      this.setState({ kind: "blocked", pending, blocked: this.stuck.size });
    } else if (this.failures > 0) {
      this.setState({ kind: "offline", pending });
    } else {
      this.setState({ kind: "saved", pending });
    }
    return this.state;
  }

  /**
   * Drain the queue, retrying with backoff until it is empty or everything left is stuck.
   *
   * Re-entrant by design: taps arrive faster than requests complete, so a second call while
   * a drain is in flight sets a flag and the running drain loops once more rather than
   * starting a competing one that would post the same events twice.
   *
   * The flag is checked again after the run returns, not only inside it. A run that has found
   * the queue empty still awaits the disk to settle the pill, and a ball tapped in that moment
   * would otherwise wait for the next trigger — on a page, the thirty-second interval.
   */
  async drain(): Promise<void> {
    if (this.draining !== null) {
      this.again = true;
      return this.draining;
    }
    this.draining = (async (): Promise<void> => {
      try {
        do {
          this.again = false;
          await this.guarded();
        } while (this.again);
      } finally {
        this.draining = null;
      }
    })();
    return this.draining;
  }

  /**
   * `run`, under the injected lock if there is one.
   *
   * If another context already holds it, this one reports what is on disk and leaves. Any
   * event that lands in the instant between the holder's last look and its release is picked
   * up by the next trigger — the next ball, the interval, or the next time the app is shown.
   */
  private async guarded(): Promise<void> {
    if (this.withLock === undefined) return this.run();
    const ran = await this.withLock(this.matchId, () => this.run());
    if (!ran) await this.refresh();
  }

  /** Called when the platform says the network is back. Retries immediately. */
  async onNetworkOnline(): Promise<void> {
    // Reset the backoff rather than waiting out a 30-second window that is now pointless.
    // Aggressive retry the moment the network returns is the difference between the pill
    // clearing while the scorer is still looking at it and it clearing a minute later.
    this.failures = 0;
    // Signing in, or a network that has come back, is the one honest reason to ask again
    // after a 401. Nothing else lifts the block, so a lapsed session costs one request, not
    // one per ball.
    this.authBlocked = false;
    // A network change is the one honest reason to reconsider a given-up event: the
    // operator may have fixed whatever refused it. One extra request per online event is
    // cheap, and never retrying means a recoverable ball stays stuck forever.
    this.stuck.clear();
    await this.drain();
  }

  private async run(): Promise<void> {
    for (;;) {
      this.again = false;
      if (this.authBlocked) {
        await this.refresh();
        return;
      }
      const unsent = await this.unsent(this.stuck);

      if (unsent.length === 0) {
        if (this.again) continue;
        await this.refresh();
        return;
      }

      this.setState({ kind: "syncing", pending: unsent.length });

      const batch = unsent.slice(0, this.batchSize);
      let response: AppendEventsResponse;
      try {
        response = await this.transport.append(this.matchId, batch);
      } catch (error) {
        if (error instanceof SyncAuthError) {
          // Not a network failure and not worth a retry. Everything stays where it is.
          this.authBlocked = true;
          await this.refresh();
          return;
        }
        // A transport failure says nothing about whether the events landed, so nothing is
        // marked and nothing is dropped. The next attempt re-posts them and the server
        // dedupes on `eventId` — which is the whole reason the client owns identity.
        this.failures += 1;
        await this.refresh();
        if (this.failures >= this.maxFailures) return;
        const delay = backoffDelay(this.failures, this.backoff);
        await this.sleep(delay);
        continue;
      }

      this.failures = 0;
      if (response.gaps.length > 0) this.events.onGaps?.(response.gaps);

      const progressed = await this.applyAcks(batch, response.acks);

      // Nothing moved and nothing is retryable: stop rather than spin. The events stay on
      // disk, unsynced, and the pill keeps saying so.
      if (!progressed && !this.again) {
        await this.refresh();
        return;
      }
    }
  }

  /** Events on disk that still need sending, in ledger order, minus the ones given up on. */
  private async unsent(stuck: ReadonlySet<EventId>): Promise<readonly Event[]> {
    const all = await this.store.getAll(this.matchId);
    const held = new Set([...this.foreignIds(all), ...this.unverifiableIds(all)]);
    return all
      .filter(
        (record) =>
          record.syncedAt === null &&
          !stuck.has(record.event.eventId) &&
          !held.has(record.event.eventId),
      )
      .map((record) => record.event);
  }

  /**
   * Unsent events that have an owner, while nobody is known to be signed in and the caller
   * asked not to send those blind (`ownerRequired`). Held, not foreign: nothing says they are
   * the wrong account's, only that it cannot be checked yet.
   */
  private unverifiableIds(all: readonly StoredEvent[]): EventId[] {
    if (!this.ownerRequired || this.owner() !== null) return [];
    return all
      .filter((record) => record.syncedAt === null && record.writtenBy !== null)
      .map((record) => record.event.eventId);
  }

  /**
   * Unsent events written by an account other than the one signed in.
   *
   * Only a positive mismatch counts. An event with no recorded owner, or a moment when the
   * signed-in user is unknown, makes no claim either way — holding balls back on a guess
   * would strand exactly the scorer this exists to protect.
   */
  private foreignIds(all: readonly StoredEvent[]): EventId[] {
    const owner = this.owner();
    if (owner === null) return [];
    return all
      .filter(
        (record) =>
          record.syncedAt === null && record.writtenBy !== null && record.writtenBy !== owner,
      )
      .map((record) => record.event.eventId);
  }

  /**
   * Delete the unsent events another account wrote. Returns how many.
   *
   * The caller must have asked the scorer first: this is the one place the outbox removes
   * anything, and it is never reached from a drain.
   */
  async discardForeign(): Promise<number> {
    const ids = this.foreignIds(await this.store.getAll(this.matchId));
    if (ids.length > 0) await this.store.remove(this.matchId, ids);
    await this.refresh();
    return ids.length;
  }

  /**
   * Apply one response, per event.
   *
   * Returns whether anything changed — an ack recorded or an event renumbered. If a whole
   * batch comes back with nothing actionable, the caller stops instead of looping.
   */
  private async applyAcks(batch: readonly Event[], acks: readonly EventAck[]): Promise<boolean> {
    const stuck = this.stuck;
    const byId = new Map(batch.map((event) => [event.eventId, event]));
    const ackedIds: EventId[] = [];
    let renumbered = false;

    for (const ack of acks) {
      const event = byId.get(ack.eventId);
      // An ack for something we did not send. Ignored rather than trusted: acting on it
      // could mark an event synced that was never posted.
      if (event === undefined) continue;

      if (isAccepted(ack)) {
        // `stored` and `duplicate` are both success. A duplicate means an earlier attempt
        // landed and only the response was lost, which is exactly what idempotency buys.
        ackedIds.push(ack.eventId);
        continue;
      }

      if (ack.reason === "seq_conflict") {
        const known = await this.holdsKnownEvent(ack.heldBy);
        if (known) {
          // This device chose an address it had already used — the undo-after-sync case.
          // Renumbering is the fix; dropping would lose the ball and resending unchanged
          // would be refused forever.
          await this.renumber(event);
          renumbered = true;
          continue;
        }
        // Held by an id this device has never seen: another writer owns this innings, and
        // no amount of retrying resolves that.
        stuck.add(ack.eventId);
        this.events.onStuck?.(event, ack);
        continue;
      }

      // malformed / unstorable / unauthorized / unknown_match: retrying cannot help.
      stuck.add(ack.eventId);
      this.events.onStuck?.(event, ack);
    }

    if (ackedIds.length > 0) {
      await this.store.markSynced(this.matchId, ackedIds, this.now().toISOString());
    }

    if (stuck.size >= MAX_CONFLICT_RENUMBERS) return false;
    return ackedIds.length > 0 || renumbered;
  }

  /** Whether the id holding a contested address is one this device already has on disk. */
  private async holdsKnownEvent(heldBy: EventId | null): Promise<boolean> {
    if (heldBy === null) return false;
    const all = await this.store.getAll(this.matchId);
    return all.some((record) => record.event.eventId === heldBy);
  }

  /**
   * Give an event the next free `seq` in its innings and rewrite it on disk.
   *
   * The `eventId` is deliberately kept. It is the identity the server dedupes on, so
   * reusing it means that if the original attempt did land after all, the resend is
   * recognised as a duplicate rather than stored as a second ball.
   */
  private async renumber(event: Event): Promise<void> {
    const all = await this.store.getAll(this.matchId);
    const highest = all.reduce(
      (max, record) =>
        record.event.inningsNo === event.inningsNo ? Math.max(max, record.event.seq) : max,
      0,
    );
    await this.store.append(this.matchId, { ...event, seq: highest + 1 } as Event);
  }
}

/**
 * The next `seq` to assign in an innings, from what is on disk.
 *
 * Exported because the *writer* needs it too and must agree with the outbox exactly. It is
 * `highest + 1` and never `count + 1`: counting is only correct while nothing has ever been
 * removed or renumbered, and both happen — an un-synced undo pops a row, and a
 * `seq_conflict` rewrites one. Counting after either hands out an address that is already
 * taken, which the server refuses and which is how a ball gets lost.
 */
export function nextSeq(records: readonly StoredEvent[], inningsNo: InningsNo): number {
  const highest = records.reduce(
    (max, record) => (record.event.inningsNo === inningsNo ? Math.max(max, record.event.seq) : max),
    0,
  );
  return highest + 1;
}

/** Whether this event has been acked by the server. */
export const isSynced = (records: readonly StoredEvent[], eventId: EventId): boolean =>
  records.some((record) => record.event.eventId === eventId && record.syncedAt !== null);
